import React from 'react';
import useWindowSize from 'lib/use-window-resize';
import { useDebouncedCallback } from '@react-hookz/web';
import Page from 'components/page/page';
import PageHeader from 'components/page/page-header';
import PageSection from 'components/page/page-section';
import { Columns, ColumnsItem } from 'components/columns';
import lexemes from 'components/lexemes';
import GuidesMobile from 'components/guides/guides-mobile';
import GuidesSidebar from 'components/guides-sidebar/guides-sidebar';
import Cards, { CardsTypes } from 'components/cards/cards';
import Card from 'components/card/card';
import { FormattedText, Header1, Header2 } from 'components/typography';
import SearchField from 'components/search-field/search-field';
import { GuideExtended } from 'schema/srv/guides';
import { GuidesMobileItemProps } from 'components/guides/guides-mobile-item.type';
import Breadcrumbs from 'components/breadcrumbs/breadcrumbs';
// import { Transition } from 'react-transition-group';
import PageFooter from 'components/page/page-footer';
import PageCookie from 'components/page-cookie/page-cookie';
import { Navigation } from 'schema/frontend/types';
import useIsomorphicLayoutEffect from 'lib/use-isomorphic-layout-effect';

type PageGuidesPropsType = {
    pageSectionUrl?: string;
    pageTitle?: string;
    pageBackgroundColor: string;
    moreGuides?: GuideExtended[] | undefined[];
    sectionTitle?: string;
    articleTitle?: string;
    articleText?: string;
    desktopAccordionContent?: GuidesMobileItemProps[];
    mobileAccordionContent?: GuidesMobileItemProps[];
    navigation: Navigation;
};

const PageGuides: React.FC<PageGuidesPropsType> = ({
    pageSectionUrl,
    pageTitle,
    pageBackgroundColor,
    moreGuides,
    sectionTitle,
    articleTitle,
    articleText,
    desktopAccordionContent,
    mobileAccordionContent,
    navigation,
}) => {
    const size = useWindowSize();
    const [pageColor, setPageColor] = React.useState('transparent');
    const [mobileMode, setMobileMode] = React.useState<boolean | undefined>(undefined);
    const sectionMargin = mobileMode ? 32 : 40;
    const secondNavRef = React.useRef<HTMLDivElement>(null);
    const [landscapeMode, setLandscapeMode] = React.useState<boolean | undefined>(undefined);
    const [scrollTop, setScrollTop] = React.useState(0);

    React.useEffect(() => {
        if (size?.width && size?.height) {
            if (size.width < 768) {
                setMobileMode(true);
            } else if (size.height < 460) {
                setMobileMode(true);
            } else {
                setMobileMode(false);
            }
        }

        if (size?.width && size?.height && size?.width > size?.height) {
            setLandscapeMode(true);
        } else {
            setLandscapeMode(false);
        }
    }, [size]);

    React.useEffect(() => {
        if (pageBackgroundColor) {
            setPageColor(pageBackgroundColor);
            /**
             * set page background color
             */
            document.documentElement.style.setProperty('--page-bg-color', `${pageBackgroundColor}`);
        }
    }, [pageBackgroundColor]);

    // return back to remove layout shift on guides page
    useIsomorphicLayoutEffect(() => {
        /**
         * set page second navigation height
         */
        if (!mobileMode && secondNavRef.current?.clientHeight) {
            document.documentElement.style.setProperty(
                '--page-second-nav-height',
                `${secondNavRef.current?.clientHeight}px`
            );
        }

        // we need size here in deps for proper onScreenResize changes
    }, [secondNavRef, mobileMode, size, scrollTop]);

    const onScroll = useDebouncedCallback(
        (event) => {
            setScrollTop(event.target.documentElement.scrollTop);
        },
        [],
        1
    );

    React.useEffect(() => {
        if (!landscapeMode) {
            window.addEventListener('scroll', onScroll);
        }

        return () => {
            if (!landscapeMode) {
                window.removeEventListener('scroll', onScroll);
            }
        };
    }, [onScroll, landscapeMode]);

    const guidePageBreadcrumb = pageTitle ? `${pageTitle} - ` : ' ';

    const breadcrumbsList = [];
    breadcrumbsList.push({
        url: articleTitle ? pageSectionUrl : undefined,
        label: `${guidePageBreadcrumb}${sectionTitle}` || '',
    });
    if (articleTitle) {
        breadcrumbsList.push({ label: articleTitle });
    }

    const setMoreGuides = (item: GuideExtended | undefined, index: number) => {
        if (item) {
            return (
                <React.Fragment key={index}>
                    <Card
                        title={item?.title}
                        text={item?.text}
                        imageUrl={item?.imageUrl}
                        backgroundColor={item?.backgroundColor}
                        url={item?.url}
                    />
                </React.Fragment>
            );
        }
        return null;
    };

    const moreGuidesMobileSection = (
        <PageSection theme={PageSection.Theme.NONE} backgroundColor="#fff" tag="section">
            <Columns vAlignTop={true} hasMobileLayout={true}>
                <ColumnsItem size={ColumnsItem.size.fluid}>
                    <Header1
                        color="var(--color-primary-01)"
                        isResponsive={true}
                        extraClass="is-mobile-first"
                        tag="h2"
                    >
                        {lexemes.guides.lxBlockMoreGuidesTitle()}
                    </Header1>
                </ColumnsItem>
                <ColumnsItem size={ColumnsItem.size.fluid} columnsItemGap={sectionMargin}>
                    <Cards type={CardsTypes.MORE_GUIDES}>
                        <>{moreGuides && moreGuides.map(setMoreGuides)}</>
                    </Cards>
                </ColumnsItem>
            </Columns>
        </PageSection>
    );

    const activeMobileContent =
        mobileAccordionContent && mobileAccordionContent.findIndex((item) => item.isActive);

    const mobileContent = (
        <>
            {mobileAccordionContent && (
                <PageSection
                    theme={PageSection.Theme.NONE}
                    noHorizontalPadding={mobileMode}
                    noBottomPadding={true}
                    noTopPadding={true}
                >
                    <Columns vAlignTop={true} hasMobileLayout={true}>
                        <ColumnsItem size={ColumnsItem.size.fluid} isBlock={true}>
                            <div style={{ marginBottom: 32 * 0.5, padding: '0 19px' }}>
                                <Breadcrumbs list={breadcrumbsList} />
                            </div>
                            <div style={{ margin: '0 16px' }}>
                                <Header1
                                    color="var(--color-primary-01)"
                                    extraClass="is-mobile-first"
                                    tag="h1"
                                >
                                    {sectionTitle}
                                </Header1>
                            </div>
                        </ColumnsItem>
                        <ColumnsItem
                            size={ColumnsItem.size.fluid}
                            isBlock={true}
                            columnsItemGap={sectionMargin}
                        >
                            <GuidesMobile
                                content={mobileAccordionContent}
                                activeItem={activeMobileContent}
                            />
                        </ColumnsItem>
                    </Columns>
                </PageSection>
            )}
            {moreGuidesMobileSection}
        </>
    );
    const content = (
        <>
            {mobileMode ? (
                mobileContent
            ) : (
                <div className="page__article-wrap">
                    <div className="page__article">
                        <article className="article">
                            <header className="article__header">
                                <Header2 tag="h2">{articleTitle}</Header2>
                            </header>

                            {articleText ? (
                                <div className="article__formatted-text">
                                    <FormattedText>{articleText}</FormattedText>
                                </div>
                            ) : null}
                        </article>

                        <section className="page__more-guides">
                            <Header2 color="var(--color-primary-01)" tag="h2">
                                {lexemes.guides.lxBlockMoreGuidesTitle()}
                            </Header2>
                            <Cards type={CardsTypes.MORE_GUIDES}>
                                <>{moreGuides && moreGuides.map(setMoreGuides)}</>
                            </Cards>
                        </section>
                    </div>
                </div>
            )}
        </>
    );
    const mobileSearchField = (
        <div
            style={{
                margin: '16px 14px 24px',
            }}
        >
            {!landscapeMode ? (
                <SearchField isFullWidth={true} hasContrast={true} isMobile={true} />
            ) : null}
        </div>
    );
    const desktopSecondNavigation = (
        <div
            style={{
                backgroundColor: pageColor,
                // optical alignment for menu
                padding: mobileMode ? '0 11px' : `0 ${40 - 3}px`,
            }}
            className="article__section-header"
            ref={secondNavRef}
        >
            <div style={{ marginTop: 32, padding: '0 3px' }}>
                <Breadcrumbs list={breadcrumbsList} />
            </div>
            <div
                style={{
                    marginTop: 40,
                    marginBottom: 42,
                    width: '100%',
                }}
            >
                <Header1 color="var(--color-primary-01)" isResponsive={true} tag="h1">
                    {sectionTitle}
                </Header1>
            </div>
        </div>
    );
    const sidebar = mobileMode ? null : (
        <nav style={{ backgroundColor: pageColor }} aria-label={pageTitle}>
            {desktopAccordionContent && <GuidesSidebar content={desktopAccordionContent} />}
        </nav>
    );

    return (
        <Page
            header={
                <div className="page__double-nav">
                    <PageHeader
                        bgColor={pageColor}
                        navigation={navigation}
                        hasBackgroundColor={true}
                        hasPhysicalSpace={true}
                        hasBorder={true}
                        hasLogoUrl={true}
                    />
                    {mobileMode ? mobileSearchField : desktopSecondNavigation}
                </div>
            }
            sidebar={sidebar}
            content={content}
            footer={<PageFooter />}
            backgroundColor={pageBackgroundColor}
            contentBackgroundColor={mobileMode ? pageBackgroundColor : '#fff'}
            isFlexible={!mobileMode}
            // let's use it for styles only for that page
            extraClass="page--guides"
            cookieNotification={<PageCookie />}
        />
    );
};

export default PageGuides;
